import * as Sentry from '@sentry/react'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { handleTrackEnterpriseFeatureModalOpen } from '../api/Analytics'
import { apiFetchPlanTierListObject } from '../api/PlanTier'

import { EnterpriseNudgeFeature } from '../utils/Interfaces'

import ImageEditingEnterpriseNudgeImage from '../assets/apply_image_edits_in_bulk.png'
import CustomWriterEnterpriseNudgeImage from '../assets/custom-writer-on-enterprise-nudge.png'
import ProductEnrichmentEnterpriseNudgeImage from '../assets/product-enrichment-on-enterprise-nudge.png'
import RewriterEnterpriseNudgeImage from '../assets/rewriter-on-enterprise-nudge.png'
import SeoKeywordsEnterpriseNudgeImage from '../assets/seo-keywords-on-enterprise-nudge.png'

// region Constants
/**
 * Default header of the enterprise feature modal.
 */
const DEFAULT_HEADER = 'Ecommerce premium feature'

/**
 * Maps each enterprise nudge feature type to its respective content.
 */
const FEATURE_CONTENT_COMPONENT_MAP: Record<
  EnterpriseNudgeFeature,
  EnterpriseFeatureCustomProps
> = {
  [EnterpriseNudgeFeature.ProductDetailsEnrichment]: {
    contentProps: {
      title: 'AI product data enrichment',
      subtitle:
        'Missing product details? Our AI can enrich product attributes from an image, web pages, PDFs and more.',
      keypoints: [
        'Our AI can read from any source and turn it into structured product attributes',
        'Enrich from product images',
        'Enrich from documents like fact sheets',
        'Enrich from searching the web for up-to-date information of the same product',
        'Enrich by UPC or GTIN'
      ],
      image: ProductEnrichmentEnterpriseNudgeImage
    }
  },
  [EnterpriseNudgeFeature.SmartSuggest]: {
    contentProps: {
      title: 'SEO keywords suggestions',
      subtitle:
        'AI suggests the best long tail keywords for your product and optimize your product content for it.',
      keypoints: [
        'Climb up search engine rankings and increase product discoverability',
        'Optimize your product page for search engines like Google',
        'Optimize for retail platforms such as Amazon, Walmart, Target, and more',
        'Takes into account competitor analysis and recent trends'
      ],
      image: SeoKeywordsEnterpriseNudgeImage
    }
  },
  [EnterpriseNudgeFeature.CustomContentType]: {
    contentProps: {
      title: 'Customize any content format',
      subtitle:
        'Have specific content needs? We work with you to create bespoke solutions with our bulk workflows.',
      keypoints: [
        'Create any content in bulk eg. titles, category pages, meta content',
        'Formatting such as bullet points, word length, HTML structure',
        'Precise style guide such as banned words, punctuation, and other stylistic elements',
        'Bespoke AI model catered to your unique brand voice'
      ],
      image: CustomWriterEnterpriseNudgeImage
    }
  },
  [EnterpriseNudgeFeature.ProductDescriptionRewriter]: {
    contentProps: {
      title: 'Enhanced access to product descriptions rewriter',
      subtitle:
        'Rewrite descriptions to boost SEO, ensure uniqueness, adjust brand voice and more.',
      keypoints: [
        'Increased generation limits',
        'Custom inputs for AI to use in description generation',
        'Custom formatting output such as bullet points, word length and more',
        'Custom trained AI model catered to your unique brand voice. Used by fortune 500 brands'
      ],
      image: RewriterEnterpriseNudgeImage
    }
  },
  [EnterpriseNudgeFeature.ImageEditing]: {
    headerProps: 'AI product images',
    contentProps: {
      title: 'You’ve reached the editing limit for our Beta trial!',
      subtitle:
        'If you like what you’ve tried so far, there’s so much more you can do with our AI image editor:',
      keypoints: [
        'Create lifestyle images that resonate with your brand',
        'Turn poorly taken photos into studio-quality images',
        'Remove unwanted objects',
        'Standardize images in bulk — resize, position, and up resolution',
        'Optimize images for SEO with auto-generated alt text and image renaming'
      ],
      image: ImageEditingEnterpriseNudgeImage
    }
  }
}
// endregion

// region Interfaces
export interface EnterpriseFeatureModalBodyComponentProps {
  /**
   * Main title of the enterprise feature modal.
   */
  title: string
  /**
   * Description of the enterprise feature modal.
   */
  subtitle: string
  /**
   * Bullet points to list out within the enterprise feature modal.
   */
  keypoints: string[]
  /**
   * Display image to show within the enterprise feature modal.
   */
  image: string
}

interface EnterpriseFeatureModalBaseProps {
  /**
   * Source element triggering the nudge. Used for tracking.
   */
  elementId: string
  /**
   * The window location of the element triggering the nudge. Used for tracking.
   *
   * If left unspecified, defaults to `window.location.pathname`.
   */
  pathname?: string
}

interface EnterpriseFeatureNameProps {
  /**
   * The type of enterprise feature to nudge for.
   * If specified, this will be used to determine the content of the modal.
   *
   * @example EnterpriseNudgeFeature.ProductDetailsEnrichment
   */
  featureName: EnterpriseNudgeFeature
}

interface EnterpriseFeatureCustomProps {
  /**
   * Header props for the header component of the enterprise feature modal.
   */
  headerProps?: string
  /**
   * Content props for the body component of the enterprise feature modal.
   */
  contentProps: EnterpriseFeatureModalBodyComponentProps
}

interface EnterpriseFeatureModalBaseStore {
  /**
   * Whether the modal is open or not
   */
  open: boolean
  /**
   * Header props for the header component of the enterprise feature modal.
   */
  headerProps: string
  /**
   * The plan list ID of the user
   */
  planListId: string
  /**
   * The element ID that is to be passed directly to the enterprise feature modal
   */
  elementId: string
  /**
   * Content props for the body component of the enterprise feature modal.
   */
  contentProps?: EnterpriseFeatureModalBodyComponentProps
  /**
   * Opens the modal and sets the props to be sent to the modal
   */
  openModal: (
    modalProps: EnterpriseFeatureModalProps | EnterpriseFeatureCustomModalProps
  ) => void
  /**
   * Closes the modal and resets the props
   */
  closeModal: () => void
}

interface EnterpriseFeatureModalCTAProps {
  /**
   * The function to be called when the user clicks on the CTA button on the enterprise feature modal.
   */
  handleCTAClick?: () => void
}

type EnterpriseFeatureModalStore = EnterpriseFeatureModalBaseStore &
  EnterpriseFeatureModalCTAProps
export type EnterpriseFeatureModalProps = EnterpriseFeatureModalBaseProps &
  EnterpriseFeatureModalCTAProps &
  EnterpriseFeatureNameProps
export type EnterpriseFeatureCustomModalProps = EnterpriseFeatureModalBaseProps &
  EnterpriseFeatureModalCTAProps &
  EnterpriseFeatureCustomProps
// endregion

export const useEnterpriseFeatureModal = create(
  immer<EnterpriseFeatureModalStore>((set) => ({
    open: false,
    headerProps: DEFAULT_HEADER,
    planListId: '',
    elementId: '',
    closeModal: () => set({ open: false }),
    openModal: (
      modalProps:
        | EnterpriseFeatureModalProps
        | EnterpriseFeatureCustomModalProps
    ) => {
      const {
        elementId,
        pathname = window.location.pathname,
        handleCTAClick
      } = modalProps
      const componentProps: EnterpriseFeatureCustomProps =
        'featureName' in modalProps
          ? FEATURE_CONTENT_COMPONENT_MAP[modalProps.featureName]
          : {
              headerProps: modalProps.headerProps,
              contentProps: modalProps.contentProps
            }

      set({
        open: true,
        headerProps: componentProps.headerProps ?? DEFAULT_HEADER,
        contentProps: componentProps.contentProps,
        elementId: elementId,
        handleCTAClick: handleCTAClick
      })

      // Track feature modal open event
      apiFetchPlanTierListObject()
        .then((planTierList) => {
          const planListId = planTierList.plan_list_id
          handleTrackEnterpriseFeatureModalOpen({
            planListId: planListId,
            elementId: elementId,
            pathname: pathname
          })
          set({ planListId: planListId })
        })
        .catch((error) => {
          Sentry.captureException(error, { level: 'error' })
        })
    }
  }))
)
